'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
  useTheme
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { IconUpload, IconTrash, IconMicrophone, IconPlayerPause, IconPlayerStop, IconRefresh } from '@tabler/icons-react';

import { useVoicemailDrops, useCreateVoicemailDrop, useReplaceVoicemailDrop, useDeleteVoicemailDrop } from 'hooks/query/useVoicemailDrops';
import useTeammateId from 'hooks/useTeammateId';
import { getAudioDuration, formatDuration } from 'utils/audio-duration';
import { VoicemailDrop } from 'types/user-settings';
import { dispatch } from 'store';
import { openSnackbar } from 'store/slices/snackbar';

const ACCEPTED_AUDIO_TYPES = 'audio/mpeg,audio/wav,audio/mp4,audio/ogg,audio/x-m4a,audio/webm,.mp3,.wav,.m4a,.ogg';

// Preferred MIME types for MediaRecorder, in order of preference
const RECORDER_MIME_CANDIDATES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus', 'audio/mp4'];

function getSupportedMimeType(): string | undefined {
  if (typeof MediaRecorder === 'undefined') return undefined;
  return RECORDER_MIME_CANDIDATES.find((mime) => MediaRecorder.isTypeSupported(mime));
}

function blobToFile(blob: Blob, fileName: string): File {
  return new File([blob], fileName, { type: blob.type, lastModified: Date.now() });
}

// Map MIME types to filename extensions for the file we ship to the backend.
// The backend transcodes everything to MP3 before storing in S3, so this
// extension is purely cosmetic — it lives in the original `fileName` column
// for display purposes. The map covers everything multer accepts on the
// server (audio-multer.config.ts) plus the four MIME types MediaRecorder is
// likely to produce in the browser.
const EXTENSION_BY_MIME: Record<string, string> = {
  'audio/webm': 'webm',
  'audio/ogg': 'ogg',
  'audio/mp4': 'mp4',
  'audio/x-m4a': 'm4a',
  'audio/m4a': 'm4a',
  'audio/mpeg': 'mp3',
  'audio/wav': 'wav',
  'audio/x-wav': 'wav'
};

// Strip only the characters that are illegal in Windows / macOS / POSIX
// filenames (path separators, control chars, reserved metacharacters).
// Apostrophes, parentheses, periods, accented characters, etc. are kept
// because they're valid filename characters and the user expects to see
// "John's voicemail (v2)" come back as "John's voicemail (v2)", not
// "Johns voicemail v2".
const ILLEGAL_FILENAME_CHARS = /[\\/:*?"<>|\x00-\x1f\x7f]/g;

// ─── Inline Recorder ────────────────────────────────────────────────

/**
 * `idle` asks for the microphone and starts immediately; `ready` waits for the
 * person to press Record. Which one a recorder opens in is `autoStart`.
 */
type RecorderState = 'ready' | 'idle' | 'recording' | 'paused' | 'preview';

interface InlineRecorderProps {
  name: string;
  onSave: (file: File, duration: number) => void;
  onCancel: () => void;
  disabled?: boolean;
  /**
   * DEFAULTS TO TRUE, which is what this component has always done: mount, ask
   * for the microphone, start capturing. That is right where the recorder only
   * appears after somebody pressed "Record another greeting" — the press IS the
   * intent.
   *
   * The Power Dialer opens it behind a TAB, where clicking "Record" means "show
   * me the recorder", not "start recording now" — a person who clicks to look
   * around finds themselves already being taped. `false` opens on a Record
   * button and waits.
   */
  autoStart?: boolean;
  /** Adds Pause/Resume. Off by default so the settings page is unchanged. */
  allowPause?: boolean;
}

// ─── Waveform Canvas ────────────────────────────────────────────────

function WaveformVisualizer({ analyser }: { analyser: AnalyserNode | null }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animFrameRef = useRef<number>(0);
  const theme = useTheme();

  useEffect(() => {
    if (!analyser || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    const errorColor = theme.palette.error.main;

    const draw = () => {
      animFrameRef.current = requestAnimationFrame(draw);
      analyser.getByteTimeDomainData(dataArray);

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.lineWidth = 2;
      ctx.strokeStyle = errorColor;
      ctx.beginPath();

      const sliceWidth = canvas.width / bufferLength;
      let x = 0;

      for (let i = 0; i < bufferLength; i++) {
        const v = dataArray[i] / 128.0;
        const y = (v * canvas.height) / 2;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
        x += sliceWidth;
      }

      ctx.lineTo(canvas.width, canvas.height / 2);
      ctx.stroke();
    };

    draw();

    return () => {
      cancelAnimationFrame(animFrameRef.current);
    };
  }, [analyser, theme]);

  return <canvas ref={canvasRef} width={400} height={60} style={{ width: '100%', maxWidth: 400, height: 60 }} />;
}

// ─── Inline Recorder ────────────────────────────────────────────────

export function InlineRecorder({ name, onSave, onCancel, disabled, autoStart = true, allowPause = false }: InlineRecorderProps) {
  const theme = useTheme();
  const [state, setState] = useState<RecorderState>(autoStart ? 'idle' : 'ready');
  /** Why the last attempt failed, kept on screen after the toast has gone. */
  const [micError, setMicError] = useState<string | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
  const [analyserNode, setAnalyserNode] = useState<AnalyserNode | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
      if (audioCtxRef.current) audioCtxRef.current.close();
    };
  }, []);

  // Revoke the preview URL when it changes or when the component unmounts.
  // The cleanup function captures the previous previewUrl in its closure, so
  // when previewUrl changes the previous effect's cleanup runs first with the
  // old value — no extra ref tracking needed.
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const startTimer = useCallback(() => {
    stopTimer();
    timerRef.current = setInterval(() => setElapsed((prev) => prev + 1), 1000);
  }, [stopTimer]);

  const startRecording = useCallback(async () => {
    const mimeType = getSupportedMimeType();
    if (!mimeType) {
      dispatch(
        openSnackbar({
          open: true,
          message: 'Your browser does not support audio recording.',
          variant: 'alert',
          alert: { color: 'error' },
          close: false
        })
      );
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      // Set up Web Audio analyser for waveform
      const audioCtx = new AudioContext();
      audioCtxRef.current = audioCtx;
      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 2048;
      source.connect(analyser);
      setAnalyserNode(analyser);

      const recorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeType });
        const url = URL.createObjectURL(blob);

        setRecordedBlob(blob);
        setPreviewUrl(url);
        setState('preview');
        setAnalyserNode(null);

        // Stop mic access + audio context
        stream.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        audioCtx.close();
        audioCtxRef.current = null;
      };

      recorder.start(100);
      setState('recording');
      setElapsed(0);
      startTimer();
    } catch (error: any) {
      const msg =
        error?.name === 'NotAllowedError'
          ? 'Microphone access was denied. Please allow microphone access in your browser settings.'
          : 'Failed to access microphone. Please check your device settings.';
      dispatch(openSnackbar({ open: true, message: msg, variant: 'alert', alert: { color: 'error' }, close: false }));
      /*
        BACK TO THE DOOR, not stuck at it. `idle` paints "Requesting microphone
        access..." and only `startRecording` succeeding leaves it, so a rejected
        `getUserMedia` used to spin there for good — the toast said what went
        wrong and the panel never let them try again.

        `ready` even for an `autoStart` recorder: it has no door of its own, but
        offering "Start Recording" to somebody who just fixed a permission
        prompt beats a spinner that outlives the reason for it.
      */
      setMicError(msg);
      setState('ready');
    }
  }, [startTimer]);

  const stopRecording = useCallback(() => {
    stopTimer();
    mediaRecorderRef.current?.stop();
  }, [stopTimer]);

  /**
   * THE TIMER STOPS WITH THE RECORDER. `MediaRecorder.pause()` stops capturing
   * but nothing else does — leave the interval running and a 10s recording
   * paused for a minute reports 70s, which is then saved as the duration and
   * disagrees with the audio for the life of the recording.
   */
  const pauseRecording = useCallback(() => {
    if (mediaRecorderRef.current?.state !== 'recording') return;
    mediaRecorderRef.current.pause();
    stopTimer();
    setState('paused');
  }, [stopTimer]);

  const resumeRecording = useCallback(() => {
    if (mediaRecorderRef.current?.state !== 'paused') return;
    mediaRecorderRef.current.resume();
    startTimer();
    setState('recording');
  }, [startTimer]);

  const handleReRecord = useCallback(() => {
    setPreviewUrl(null);
    setRecordedBlob(null);
    setElapsed(0);
    setMicError(null);
    // 'idle' triggers the effect below, which calls startRecording() to begin a
    // new cycle. A recorder that opened on a button goes back to that button
    // instead — re-record must not mean "and start taping now" when the first
    // take did not.
    setState(autoStart ? 'idle' : 'ready');
  }, [autoStart]);

  const handleSave = useCallback(() => {
    if (!recordedBlob) return;
    // recordedBlob.type often arrives as e.g. "audio/webm;codecs=opus" — strip
    // the codecs suffix before looking up the extension. Default to 'webm'
    // (the most common MediaRecorder output) when the MIME type is unknown.
    const baseMime = recordedBlob.type.toLowerCase().split(';')[0];
    const ext = EXTENSION_BY_MIME[baseMime] ?? 'webm';
    const safeName = name.replace(ILLEGAL_FILENAME_CHARS, '').trim() || 'recording';
    const file = blobToFile(recordedBlob, `${safeName}.${ext}`);
    onSave(file, elapsed);
  }, [recordedBlob, elapsed, onSave, name]);

  const handleCancel = useCallback(() => {
    if (mediaRecorderRef.current?.state === 'recording' || mediaRecorderRef.current?.state === 'paused') {
      mediaRecorderRef.current.stop();
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (audioCtxRef.current) {
      audioCtxRef.current.close();
      audioCtxRef.current = null;
    }
    setAnalyserNode(null);
    onCancel();
  }, [onCancel]);

  // Start recording when state is 'idle' — fires on mount and on re-record
  useEffect(() => {
    if (state === 'idle') {
      startRecording();
    }
  }, [state, startRecording]);

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        border: `1px solid ${state === 'recording' ? theme.palette.error.main : theme.palette.divider}`,
        bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.50'
      }}
    >
      {/*
        The door a manual recorder opens on. `autoStart` recorders never see
        this — they are already capturing by the time they paint.
      */}
      {state === 'ready' && (
        <Stack spacing={1.5} alignItems="center">
          <Typography variant="body2" color={micError ? 'error' : 'text.secondary'} textAlign="center">
            {micError ?? 'Ready when you are.'}
          </Typography>
          <Button
            variant="contained"
            color="error"
            startIcon={<IconMicrophone size={18} />}
            disabled={disabled}
            onClick={() => {
              setMicError(null);
              setState('idle');
            }}
          >
            {micError ? 'Try again' : 'Start Recording'}
          </Button>
          <Button size="small" variant="text" color="inherit" onClick={handleCancel}>
            Cancel
          </Button>
        </Stack>
      )}

      {state === 'paused' && (
        <Stack spacing={2} alignItems="center">
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: 'text.disabled' }} />
            <Typography variant="subtitle1" fontWeight={600}>
              Paused
            </Typography>
            {/* The timer is stopped too — see `pauseRecording`. A clock that
                kept running here would be saved as the duration. */}
            <Typography variant="h4" fontWeight={700} fontFamily="monospace">
              {formatDuration(elapsed)}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={2}>
            <Button variant="contained" color="error" startIcon={<IconMicrophone size={18} />} onClick={resumeRecording}>
              Resume
            </Button>
            <Button variant="outlined" startIcon={<IconPlayerStop size={18} />} onClick={stopRecording}>
              Stop
            </Button>
          </Stack>
        </Stack>
      )}

      {state === 'recording' && (
        <Stack spacing={2} alignItems="center">
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                bgcolor: 'error.main',
                animation: 'pulse 1.5s ease-in-out infinite',
                '@keyframes pulse': {
                  '0%, 100%': { opacity: 1 },
                  '50%': { opacity: 0.3 }
                }
              }}
            />
            <Typography variant="subtitle1" fontWeight={600}>
              Recording...
            </Typography>
            <Typography variant="h4" fontWeight={700} fontFamily="monospace">
              {formatDuration(elapsed)}
            </Typography>
          </Stack>
          <WaveformVisualizer analyser={analyserNode} />
          <Stack direction="row" spacing={2}>
            {allowPause && (
              <Button variant="outlined" color="inherit" startIcon={<IconPlayerPause size={18} />} onClick={pauseRecording}>
                Pause
              </Button>
            )}
            <Button variant="contained" color="error" startIcon={<IconPlayerStop size={18} />} onClick={stopRecording}>
              Stop Recording
            </Button>
          </Stack>
        </Stack>
      )}

      {state === 'preview' && previewUrl && (
        <Stack spacing={2} alignItems="center">
          <Typography variant="subtitle2" color="text.secondary">
            Preview — {formatDuration(elapsed)}
          </Typography>
          <audio controls preload="auto" src={previewUrl} style={{ width: '100%', maxWidth: 400 }} />
          <Stack direction="row" spacing={2}>
            <Button variant="outlined" startIcon={<IconRefresh size={16} />} onClick={handleReRecord} disabled={disabled}>
              Re-record
            </Button>
            <Button variant="outlined" onClick={handleCancel}>
              Cancel
            </Button>
            <LoadingButton variant="contained" onClick={handleSave} loading={disabled}>
              Save
            </LoadingButton>
          </Stack>
        </Stack>
      )}

      {state === 'idle' && (
        <Stack spacing={1} alignItems="center">
          <CircularProgress size={20} />
          <Typography variant="caption" color="text.secondary">
            Requesting microphone access...
          </Typography>
          <Button size="small" variant="text" onClick={handleCancel}>
            Cancel
          </Button>
        </Stack>
      )}
    </Box>
  );
}

// ─── Main Section ───────────────────────────────────────────────────

interface VoicemailDropsSectionProps {
  /** The user whose settings page this is. */
  userId?: string;
}

export function VoicemailDropsSection({ userId }: VoicemailDropsSectionProps) {
  // An admin setting up a teammate manages THAT teammate's library (Rec0CB6ULP7C5).
  const teammateId = useTeammateId(userId);
  const theme = useTheme();
  const { data: drops = [], isLoading } = useVoicemailDrops(teammateId);
  const createMutation = useCreateVoicemailDrop(teammateId);
  const replaceMutation = useReplaceVoicemailDrop(teammateId);
  const deleteMutation = useDeleteVoicemailDrop(teammateId);

  const [newName, setNewName] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<VoicemailDrop | null>(null);
  const [showRecorder, setShowRecorder] = useState(false);
  // Replace state: which drop is being replaced, and whether recording or uploading
  const [replaceTarget, setReplaceTarget] = useState<VoicemailDrop | null>(null);
  const [replaceRecording, setReplaceRecording] = useState(false);
  const addFileRef = useRef<HTMLInputElement>(null);
  const replaceFileRef = useRef<HTMLInputElement>(null);

  // ── Create handlers ──

  const handleUploadFile = () => {
    if (!newName.trim()) return;
    addFileRef.current?.click();
  };

  const handleAddFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    let duration: number | undefined;
    try {
      duration = await getAudioDuration(file);
    } catch (error) {
      // Duration extraction failed — non-fatal, the upload still goes
      // through without duration metadata. Logged so that recurring
      // failures (e.g. an unsupported format we forgot to handle in
      // getAudioDuration) are visible in the browser console.
      // eslint-disable-next-line no-console
      console.warn('[VoicemailDrops] failed to extract audio duration on upload', {
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
        error
      });
    }

    createMutation.mutate(
      { name: newName.trim(), file, duration },
      {
        onSuccess: () => setNewName('')
      }
    );

    e.target.value = '';
  };

  const handleStartRecording = () => {
    if (!newName.trim()) return;
    setShowRecorder(true);
  };

  const handleRecordingSave = (file: File, durationSeconds: number) => {
    createMutation.mutate(
      { name: newName.trim(), file, duration: durationSeconds },
      {
        onSuccess: () => {
          setNewName('');
          setShowRecorder(false);
        },
        onError: () => {
          setShowRecorder(false);
        }
      }
    );
  };

  const handleRecordingCancel = () => {
    setShowRecorder(false);
  };

  // ── Replace handlers ──

  const handleReplaceUpload = () => {
    replaceFileRef.current?.click();
  };

  const handleReplaceFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !replaceTarget) return;

    let duration: number | undefined;
    try {
      duration = await getAudioDuration(file);
    } catch (error) {
      // Duration extraction failed — non-fatal, see handleAddFileSelected.
      // eslint-disable-next-line no-console
      console.warn('[VoicemailDrops] failed to extract audio duration on replace', {
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
        error
      });
    }

    replaceMutation.mutate({ id: replaceTarget.id, file, duration }, { onSettled: () => setReplaceTarget(null) });
    e.target.value = '';
  };

  const handleReplaceRecord = () => {
    setReplaceRecording(true);
  };

  const handleReplaceRecordingSave = (file: File, durationSeconds: number) => {
    if (!replaceTarget) return;
    replaceMutation.mutate(
      { id: replaceTarget.id, file, duration: durationSeconds },
      {
        onSettled: () => {
          setReplaceTarget(null);
          setReplaceRecording(false);
        }
      }
    );
  };

  const handleReplaceCancel = () => {
    setReplaceTarget(null);
    setReplaceRecording(false);
  };

  // ── Delete handler ──

  const handleDeleteConfirm = () => {
    if (!deleteTarget) return;
    deleteMutation.mutate(deleteTarget.id, {
      onSettled: () => setDeleteTarget(null)
    });
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={3}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Upload pre-recorded voicemails for one-click VM drops during calls.
      </Typography>

      {/* Voicemail Drops List */}
      <Stack spacing={1.5} mb={3}>
        {drops.map((drop) => {
          const isReplacing = replaceTarget?.id === drop.id;

          return (
            <Box key={drop.id}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.50',
                  border: `1px solid ${theme.palette.divider}`
                }}
              >
                {/* Audio Player */}
                <audio preload="none" controls style={{ height: 36, minWidth: 200, flex: '0 0 auto' }}>
                  <source src={drop.audioUrl} type={drop.mimeType || 'audio/mpeg'} />
                </audio>

                {/* Info */}
                <Box flex={1} minWidth={0}>
                  <Typography variant="subtitle1" fontWeight={600} noWrap>
                    {drop.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {drop.fileName} &middot; {formatDuration(drop.duration)}
                  </Typography>
                </Box>

                {/* Replace Button */}
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<IconUpload size={16} />}
                  onClick={() => setReplaceTarget(isReplacing ? null : drop)}
                  disabled={replaceMutation.isPending}
                >
                  {isReplacing ? 'Cancel' : 'Replace'}
                </Button>

                {/* Delete Button */}
                <IconButton size="small" color="error" onClick={() => setDeleteTarget(drop)} disabled={deleteMutation.isPending}>
                  <IconTrash size={18} />
                </IconButton>
              </Box>

              {/* Replace options — shown below the drop row */}
              {isReplacing && !replaceRecording && (
                <Box sx={{ mt: 1, ml: 2 }}>
                  <Stack direction="row" spacing={1.5}>
                    <Button size="small" variant="contained" startIcon={<IconMicrophone size={16} />} onClick={handleReplaceRecord}>
                      Record New
                    </Button>
                    <Button size="small" variant="outlined" startIcon={<IconUpload size={16} />} onClick={handleReplaceUpload}>
                      Upload File
                    </Button>
                    <input type="file" accept={ACCEPTED_AUDIO_TYPES} hidden ref={replaceFileRef} onChange={handleReplaceFileSelected} />
                  </Stack>
                </Box>
              )}

              {/* Replace recorder */}
              {isReplacing && replaceRecording && (
                <Box sx={{ mt: 1 }}>
                  <InlineRecorder
                    name={drop.name}
                    onSave={handleReplaceRecordingSave}
                    onCancel={handleReplaceCancel}
                    disabled={replaceMutation.isPending}
                  />
                </Box>
              )}
            </Box>
          );
        })}
      </Stack>

      {/* Add New Voicemail */}
      {!showRecorder ? (
        <Stack spacing={2}>
          <TextField
            size="small"
            label="New Voicemail Name"
            placeholder="e.g. Holiday Greeting"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            sx={{ maxWidth: 350 }}
          />
          <Stack direction="row" spacing={1.5}>
            <LoadingButton
              variant="contained"
              startIcon={<IconMicrophone size={18} />}
              loading={createMutation.isPending}
              onClick={handleStartRecording}
              disabled={!newName.trim()}
            >
              Record
            </LoadingButton>
            <LoadingButton
              variant="outlined"
              startIcon={<IconUpload size={18} />}
              loading={createMutation.isPending}
              onClick={handleUploadFile}
              disabled={!newName.trim()}
            >
              Upload File
            </LoadingButton>
            <input type="file" accept={ACCEPTED_AUDIO_TYPES} hidden ref={addFileRef} onChange={handleAddFileSelected} />
          </Stack>
        </Stack>
      ) : (
        <InlineRecorder
          name={newName.trim()}
          onSave={handleRecordingSave}
          onCancel={handleRecordingCancel}
          disabled={createMutation.isPending}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete Voicemail Drop</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete &quot;{deleteTarget?.name}&quot;? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} variant="outlined">
            Cancel
          </Button>
          <LoadingButton onClick={handleDeleteConfirm} loading={deleteMutation.isPending} variant="contained" color="error">
            Delete
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
